import moment from 'moment'
import { DateTime } from 'luxon'
import { getUserTimezone as getTimezone } from './timezoneStorage'

// Re-export getUserTimezone so other files can import from dateUtils
export { getTimezone as getUserTimezone }

// Internal helper to get user timezone
const getUserTimezone = getTimezone

/**
 * Convert moment/Date/string to ISO string for API
 * Always returns UTC ISO string
 * @param {moment.Moment|Date|string} input - Date to convert
 * @returns {string|null} ISO string or null
 */
export const toISOString = (input) => {
  if (!input) return null

  let date
  if (moment.isMoment(input)) {
    date = input.toDate()
  } else if (typeof input === 'string') {
    date = new Date(input)
  } else {
    date = input
  }

  return date.toISOString()
}

/**
 * Create date range for API queries using Luxon
 * Returns ISO strings for start and end of day in USER'S SELECTED timezone
 *
 * Takes a date (from DateTime picker or other source) and creates start/end of day
 * in the user's selected timezone. Extracts date components to avoid timezone
 * conversion issues.
 *
 * @param {moment.Moment|Date|string} date - Date to create range for
 * @returns {{start: string, end: string}} ISO strings for start and end of day
 */
export const createDateRange = (date) => {
  const userTimezone = getUserTimezone()

  // Extract date components (year, month, day) to avoid timezone conversion issues
  // When we convert moment->Date->DateTime with a different timezone, the date can shift
  let year, month, day

  if (moment.isMoment(date)) {
    year = date.year()
    month = date.month() + 1 // moment months are 0-indexed, Luxon uses 1-indexed
    day = date.date()
  } else if (typeof date === 'string') {
    const m = moment(date)
    year = m.year()
    month = m.month() + 1
    day = m.date()
  } else {
    const m = moment(date)
    year = m.year()
    month = m.month() + 1
    day = m.date()
  }

  // Create DateTime in user's timezone with extracted date components
  // This ensures we get Jan 24 in user's timezone, not a shifted date
  const dt = DateTime.fromObject(
    { year, month, day },
    { zone: userTimezone }
  )

  // Get start and end of day in user's timezone
  const startOfDay = dt.startOf('day')
  const endOfDay = dt.endOf('day')

  return {
    start: startOfDay.toISO(),
    end: endOfDay.toISO(),
  }
}

/**
 * Parse API response date to moment
 * API returns ISO strings (UTC)
 * @param {string} isoString - ISO date string from API
 * @returns {moment.Moment|null} Moment object or null
 */
export const parseAPIDate = (isoString) => {
  if (!isoString) return null
  return moment(isoString) // Moment parses ISO as UTC, converts to local
}

/**
 * Format date for display in user's timezone
 * @param {moment.Moment|Date|string} date - Date to format
 * @param {string} format - Moment format string (default: 'DD/MM/YYYY')
 * @returns {string} Formatted date string
 */
export const formatDate = (date, format = 'DD/MM/YYYY') => {
  if (!date) return ''

  // Keep using moment for formatting since it's already used throughout the app
  return moment(date).format(format)
}

/**
 * Format datetime for display in user's timezone
 * @param {moment.Moment|Date|string} date - Date to format
 * @param {string} format - Moment format string (default: 'DD/MM/YYYY HH:mm')
 * @returns {string} Formatted datetime string
 */
export const formatDateTime = (date, format = 'DD/MM/YYYY HH:mm') => {
  if (!date) return ''

  // Keep using moment for formatting since it's already used throughout the app
  return moment(date).format(format)
}

/**
 * Validate ISO date string
 * @param {string} dateString - Date string to validate
 * @returns {boolean} True if valid ISO date string
 */
export const isValidISODate = (dateString) => {
  if (!dateString) return false
  const dt = DateTime.fromISO(dateString)
  return dt.isValid
}

/**
 * Get current date/time in user's timezone
 * @returns {moment.Moment} Current moment in user's timezone
 */
export const now = () => {
  return moment()
}
